/**
 * 封面(cover)— projects / brand_kits 共用的三態門面圖
 * -----------------------------------------------------------------------------
 * 鏡射 BE `app/core/cover.py`(BE 也刻意把它收斂在 core 當 SSOT — 兩個 root entity
 * 的封面語意完全一致,各寫一份必然 drift)。契約:BE docs/92。
 *
 * 三態:`none`(未設)/ `color`(純色 `#RRGGBB`)/ `image`(WebP data URI)。
 *
 * ⚠️ **`kind` 是唯一判準,不要去判 `color` / `image` 誰有值。** 三態互斥由 BE 的
 * `model_validator` 保證,不可能兩個都有值;FE 若自己另立一套「誰優先」的判準,
 * 那條判準遲早跟後端漂移。domain 型別做成 discriminated union 就是為了讓這件事
 * 在型別層面成立 —— 消費端拿到 `kind === 'color'` 時 `color` 必為 string,不用 `!`。
 *
 * ⚠️ **圖片是 base64 data URI,不是 signed URL**:沒有過期、不用刷新、不用第二趟
 * 請求,realtime listener 的 snapshot 裡就有。跟品牌包的 `logo_urls` 是**兩件不同
 * 的東西**(logo = 餵生成端的品牌資產,三 slot / GCS / signed URL / intent+finalize
 * 兩段式);兩者在同一個回應裡但互不相關,不要合併處理。
 */

// ─── 值域(BE Literal 的鏡射;新增值時這裡要同步,契約鎖會抓)────────────────
export const COVER_KINDS = ['none', 'color', 'image'] as const;
export type CoverKind = (typeof COVER_KINDS)[number];

/**
 * `color` 唯一合法形式。BE `HEX_COLOR_PATTERN` 逐字鏡射 —— 簡寫 `#fff`、具名色
 * `red`、`rgb()` 一律不收。
 *
 * 🔒 這不只是格式檢查:`color` 會被塞進 CSS `background`,任意字串進 style 是注入面
 * (BE 的 code comment 也是這麼寫的)。FE 這側再驗一次是 defence-in-depth,對齊
 * `registryMedia`(host 白名單)/ `trustedAssetUrl`(protocol allowlist)的既有慣例。
 */
export const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * `image` 唯一合法前綴。BE `COVER_IMAGE_DATA_URI_PREFIX` 鏡射。
 *
 * 🔒 同上:`image` 會被塞進 `<img src>`。前綴白名單 fail-closed 擋掉
 * `data:text/html` 與 `data:image/svg+xml`(SVG 可嵌 script = XSS 載體;BE 的
 * logo MIME 白名單也是同一個理由剔除 SVG)。
 */
export const COVER_IMAGE_DATA_URI_PREFIX = 'data:image/webp;base64,';

// ─── DTO(wire;三個欄位都是 snake-free,形狀與 domain 相同)──────────────────

export interface CoverDto {
  kind: CoverKind;
  color: string | null;
  image: string | null;
}

/**
 * `CoverDto` 的鍵集,給契約鎖比對 BE `CoverSpec.model_fields` 用。
 *
 * `satisfies` 讓 tsc 強制窮舉:DTO 加欄卻忘了補這裡 → 編譯期就紅
 * (對齊 `LIBRARY_ITEM_DTO_KEYS` / `REPORT_DTO_KEYS` 慣例)。
 */
export const COVER_DTO_KEYS = ['kind', 'color', 'image'] as const satisfies ReadonlyArray<
  keyof CoverDto
>;

// ─── 前端 domain(discriminated union)────────────────────────────────────────

export type Cover =
  | { kind: 'none'; color: null; image: null }
  | { kind: 'color'; color: string; image: null }
  | { kind: 'image'; color: null; image: string };

/** 未設封面的正規值;mapper 的所有降級路徑都回這顆。 */
export const COVER_NONE: Cover = { kind: 'none', color: null, image: null };

/**
 * `PATCH` / `POST` body 內的 `cover` —— **只有 `none` / `color`**。
 *
 * BE 的 `CoverPatch.kind` 是 `Literal["none","color"]`(`image` 刻意不在裡面),
 * 圖片唯一寫入路徑是 `POST .../cover` multipart。這裡用一樣窄的 union,讓「想用
 * PATCH 塞圖片」變成**編譯錯誤**而不是跑到線上才吃 422。
 */
export type CoverPatch = { kind: 'none' } | { kind: 'color'; color: string };

/**
 * `CoverPatch` 的 kind 值域 —— **與 `COVER_KINDS` 是兩把,不要合併**。
 *
 * 回應三態(含 `image`)vs 請求兩態(不含)。合成一把就會讓人以為 PATCH 塞得進
 * 圖片,而那是 422。契約鎖分別對 BE 的 `CoverSpec.kind` / `CoverPatch.kind` 比對。
 */
export const COVER_PATCH_KINDS = ['none', 'color'] as const satisfies ReadonlyArray<
  CoverPatch['kind']
>;

// ─── DTO → domain ────────────────────────────────────────────────────────────

/**
 * wire → domain,**未知值一律走保守分支降級成 `none`**。
 *
 * BE 保證這顆必出且三態互斥,所以正常情況下不會有降級發生。這裡仍然驗,是因為
 * 降級的代價極低(封面是裝飾,退回既有的品牌色漸層看起來完全正常),而不驗的代價
 * 是把未經檢查的字串送進 CSS 與 `<img src>`。R240.2 那條教訓同型:未知值走保守
 * 分支,不要「看起來像就當成是」。
 *
 * 具體降級條件:
 *   - 整顆缺 / 不是物件 → `none`(BE 舊 doc 沒這欄時後端會補,這條理論上碰不到)
 *   - `kind` 不在 {none,color,image} → `none`(BE 日後加第四態時 FE 不會亂畫)
 *   - `kind==='color'` 但 `color` 不是 `#RRGGBB` → `none`
 *   - `kind==='image'` 但 `image` 沒有 WebP data URI 前綴 → `none`
 */
export function mapCoverDto(dto: CoverDto | null | undefined): Cover {
  if (!dto || typeof dto !== 'object') return COVER_NONE;

  if (dto.kind === 'color') {
    const color = dto.color;
    if (typeof color === 'string' && HEX_COLOR_PATTERN.test(color)) {
      return { kind: 'color', color, image: null };
    }
    return COVER_NONE;
  }

  if (dto.kind === 'image') {
    const image = dto.image;
    if (typeof image === 'string' && image.startsWith(COVER_IMAGE_DATA_URI_PREFIX)) {
      return { kind: 'image', color: null, image };
    }
    return COVER_NONE;
  }

  // 'none' 與任何未知值
  return COVER_NONE;
}

// ─── domain → wire ───────────────────────────────────────────────────────────

/**
 * `CoverPatch` → PATCH/POST body 的 `cover`。
 *
 * ⚠️ BE model 是 `extra="forbid"` → **`none` 的時候不可以帶 `color: null`**,
 * 多帶欄位就是 422。所以這裡不是無腦展開整顆物件。
 */
export function coverPatchToWire(patch: CoverPatch): Record<string, unknown> {
  return patch.kind === 'color' ? { kind: 'color', color: patch.color } : { kind: 'none' };
}

/** 色票輸入驗證(建立 modal 用);與 mapper 共用同一條 pattern,不另立第二套。 */
export function isCoverHex(value: string): boolean {
  return HEX_COLOR_PATTERN.test(value);
}
